import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useParticipation } from "@/api/hooks/participations";
import InvestigatorsSection from "@/components/investigators/InvestigatorsSection";
import { formatDateTime } from "@/utils/format";

const STATUS_COLOR: Record<string, "default" | "primary" | "success" | "warning"> = {
  pending_kickoff: "default",
  active: "primary",
  on_hold: "warning",
  activated: "success",
  cancelled: "default",
};

function LabeledValue({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

export default function ParticipationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: part, isLoading, error } = useParticipation(id);

  if (isLoading) return <CircularProgress />;
  if (error || !part)
    return (
      <Alert severity="error">{error?.message ?? "Participation not found."}</Alert>
    );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton
          onClick={() => navigate(`/clinops/sites/locations/${part.location_id}`)}
          aria-label="Back to location"
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <Link
              to={`/clinops/sites/locations/${part.location_id}`}
              style={{ color: "inherit" }}
            >
              {part.site_name} · {part.location_name}
            </Link>
          </Typography>
          <Typography variant="h5">{part.study_name}</Typography>
        </Box>
        <Chip
          label={`Phase ${part.current_phase} of 4`}
          color="primary"
          variant="outlined"
        />
        <Chip
          label={part.status.replace(/_/g, " ")}
          color={STATUS_COLOR[part.status] ?? "default"}
        />
      </Box>

      <Box sx={{ display: "grid", gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Summary
            </Typography>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Started">
                  {part.started_at ? formatDateTime(part.started_at) : "—"}
                </LabeledValue>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Activated">
                  {part.activated_at ? formatDateTime(part.activated_at) : "—"}
                </LabeledValue>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Created">
                  {formatDateTime(part.created_at)}
                </LabeledValue>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Updated">
                  {formatDateTime(part.updated_at)}
                </LabeledValue>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <InvestigatorsSection participationId={part.id} />
      </Box>
    </Box>
  );
}
